import { getDescendants } from '@minoru/react-dnd-treeview'
import { action, computed, makeObservable, observable } from 'mobx'

import {
  IAttachment,
  IChecklistAnswerItem,
  IChecklistItem,
  IConditionalField,
  IPermitTypeField,
  ISignature,
  ISiteLocation,
  ITypeFieldFilterOptions,
  IWorkflowStep,
  PermitFieldType,
} from '~/client/graph'
import {
  conditionalPermitFields,
  locationPermitFieldTypes,
  materialPermitFieldTypes,
  materialResourcePermitFieldTypes,
} from '~/client/src/shared/constants/PermitFieldTypeConstants'
import {
  FORM_MATERIAL_FIELDS_KEY,
  FORM_MATERIAL_RESOURCE_ASSOCIATED_FIELDS_KEY,
  FORM_MATERIAL_RESOURCE_FIELDS_KEY,
  MAX_CONDITIONAL_DEPTH_LEVEL,
  PERMIT_TABLE_FIELDS_KEY,
} from '~/client/src/shared/constants/permitTypeFieldsConstants'
import PermitChecklistConditionalKey from '~/client/src/shared/enums/PermitChecklistConditionalKey'
import QuestionnaireType from '~/client/src/shared/enums/QuestionnaireType'
import IPermitFieldByIdMapEntity from '~/client/src/shared/models/IPermitFieldByIdMapEntity'
import IPermitFieldsStore from '~/client/src/shared/models/IPermitFieldsStore'
import PermitField from '~/client/src/shared/models/PermitField'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import InitialState from '~/client/src/shared/stores/InitialState'
import BsonObjectId from '~/client/src/shared/utils/BsonObjectId'
import { matchesConditionalKey } from '~/client/src/shared/utils/conditionalKeyUtils'
import { evaluateFilters } from '~/client/src/shared/utils/filterEvaluationHelpers'
import { hasPreciseCoordinates } from '~/client/src/shared/utils/locationSnapshot'
import { getQuestionnaireOptionsByType } from '~/client/src/shared/utils/PermitInstructionsHelper'
import { isEqualDeep } from '~/client/src/shared/utils/util'
import { withPrimaryLocation } from '~/client/src/shared/utils/withPrimaryLocation'
import { removeWorkflowConditionals } from '~/client/src/shared/utils/WorkflowFieldsHelper'

export interface IPermitFormField {
  id: string
  isValid: boolean
  type: string
  caption: string
  isChanged?: boolean
  isRequired?: boolean
  isValidIgnored?: boolean
  tableId?: string
  parentInstanceId?: string
  parentValueIndex?: number
  checklistItems?: IChecklistItem[]
}

const getFieldValidationId = (
  id: string,
  index: number,
  tableRowIndex?: number,
  parentInstanceId?: string,
  parentValueIndex?: number,
): string =>
  `${id}|${index || 0}|${Number.isFinite(tableRowIndex) ? tableRowIndex : ''}|${
    parentInstanceId ? parentInstanceId : ''
  }|${Number.isFinite(parentValueIndex) ? parentValueIndex : ''}`

const CHECKLIST_TYPES = [PermitFieldType.Checklist, PermitFieldType.Question]

// Screenshot capture rewrites preciseLocation image URLs (and injects
// image-only records for georeferenced locations) on save — report metadata,
// not a user edit. Strip it before comparing, or a capture-bearing save reads
// as a location change and resets/blocks the workflow step.
export function normalizeLocationForComparison(value: any): any {
  const pl = value?.preciseLocation
  if (pl === undefined) {
    return value
  }
  if (!hasPreciseCoordinates(pl)) {
    return { ...value, preciseLocation: null }
  }
  const {
    imageUrl: _imageUrl,
    overviewImageUrl: _overviewImageUrl,
    ...coords
  } = pl
  return { ...value, preciseLocation: coords }
}

export default abstract class PermitFieldsBaseStore implements IPermitFieldsStore {
  protected readonly hiddenFieldsState = observable(new Map<string, string[]>())
  protected readonly invalidFieldsState = observable(new Set<string>())

  public abstract get fields(): IPermitFormField[]
  public abstract get previousStepsFields(): IPermitFormField[]
  public abstract get firstTwoStepsFieldIds(): Set<string>
  protected abstract get canIgnoreValidation(): boolean
  protected abstract get fieldIdsToIgnore(): string[]
  protected abstract get workflowSteps(): IWorkflowStep[]
  public abstract get currentStepFieldIds(): Set<string>
  public abstract editableEntity: IPermitFieldByIdMapEntity
  public abstract existingEntity: IPermitFieldByIdMapEntity

  public constructor(
    private readonly state: InitialState,
    protected readonly locationStore: LocationAttributesStore,
  ) {
    makeObservable(this)
  }

  @computed
  public get availableWorkflowSteps(): IWorkflowStep[] {
    return this.workflowSteps.reduce((list, step) => {
      const filteredStep: IWorkflowStep = {
        id: step.id,
        type: step.type,
        fields: step.fields.filter(f => f.isShown && !this.isFieldHidden(f.id)),
        conditionalFields: step.conditionalFields,
        workflowRuleIds: [],
        notificationFieldIds: [],
        notificationUserIds: [],
      }

      list.push(filteredStep)

      return list
    }, [] as IWorkflowStep[])
  }

  @computed
  public get canTrackFieldChanges(): boolean {
    return (
      !!this.existingEntity &&
      !this.existingEntity.isDraft &&
      !!this.editableEntity
    )
  }

  @computed
  public get areRequiredFieldsChanged(): boolean {
    if (!this.canTrackFieldChanges) {
      return false
    }

    return this.previousStepsFields?.some(
      ({ isChanged, isRequired }) => isChanged && isRequired,
    )
  }

  @computed
  public get areOptionalFieldsChanged(): boolean {
    if (!this.canTrackFieldChanges) {
      return false
    }

    return this.previousStepsFields?.some(
      ({ isChanged, isRequired }) => isChanged && !isRequired,
    )
  }

  @computed
  public get areAllFieldsValid(): boolean {
    return this.fields?.every(
      ({ isValid, isValidIgnored }) => isValidIgnored || isValid,
    )
  }

  @computed
  public get areChangedFieldsValid(): boolean {
    return this.previousStepsFields?.every(
      ({ isValid, isValidIgnored }) => isValidIgnored || isValid,
    )
  }

  public get isAnyFieldInvalid(): boolean {
    return this.invalidFieldsState.size > 0
  }

  @computed
  private get allHiddenFieldIds(): string[] {
    const fieldIds: string[] = []
    this.hiddenFieldsState.forEach(ids => fieldIds.push(...ids))
    return fieldIds
  }

  @computed
  private get conditionalFields(): IConditionalField[] {
    return this.availableWorkflowSteps.flatMap(s => s.conditionalFields) || []
  }

  @computed
  protected get filterFieldsDependencyMap(): Record<
    string,
    IPermitTypeField[]
  > {
    const dependencyMap: Record<string, IPermitTypeField[]> = {}

    const allFields = this.availableWorkflowSteps.flatMap(step => step.fields)

    allFields.forEach(field => {
      field.filterOptions?.forEach(filter => {
        if (!filter.fieldId) return

        if (!dependencyMap[filter.fieldId]) {
          dependencyMap[filter.fieldId] = []
        }

        if (dependencyMap[filter.fieldId].some(f => f.id === field.id)) {
          return
        }

        dependencyMap[filter.fieldId].push(field)
      })
    })

    return dependencyMap
  }

  public getFieldValues = <T>(
    typeFieldId: string,
    tableId?: string,
    tableRowIndex?: number,
    isExistingEntity?: boolean,
    parentInstanceId?: string,
    parentValueIndex?: number,
  ): T[] => {
    if (tableId) {
      return this.getTableFieldValues<T>(
        tableId,
        typeFieldId,
        tableRowIndex,
        isExistingEntity,
        parentInstanceId,
        parentValueIndex,
      )
    }

    const entity = isExistingEntity ? this.existingEntity : this.editableEntity
    return (
      entity.getField(typeFieldId, parentInstanceId, parentValueIndex)
        ?.values || []
    )
  }

  protected getConditionalFormFields = (
    typeField: IPermitTypeField,
    parentInstanceId?: string,
    parentValueIndex?: number,
    depthLevel = 0,
  ): IPermitFormField[] => {
    if (
      depthLevel >= MAX_CONDITIONAL_DEPTH_LEVEL ||
      !this.isConditionalField(typeField)
    ) {
      return []
    }

    const conditionalFieldsByField = this.conditionalFields.filter(
      cf => cf.fieldId === typeField.id,
    )

    if (!conditionalFieldsByField.length) {
      return []
    }

    const workflowFields = this.editableEntity.fields.filter(
      f =>
        f.fieldId === typeField.id &&
        (!parentInstanceId ||
          (f.parentInstanceId === parentInstanceId &&
            f.parentValueIndex === parentValueIndex)),
    )

    return workflowFields.flatMap(({ instanceId, type, values }) => {
      if (CHECKLIST_TYPES.includes(type)) {
        return this.assembleConditionalFields(
          typeField,
          0,
          values,
          conditionalFieldsByField,
          instanceId,
          depthLevel,
        )
      }

      return values.flatMap((_, idx) =>
        this.assembleConditionalFields(
          typeField,
          idx,
          values,
          conditionalFieldsByField,
          instanceId,
          depthLevel,
        ),
      )
    })
  }

  private assembleConditionalFields = (
    typeField: IPermitTypeField,
    idx: number,
    values: any[],
    conditionalFieldsByField: IConditionalField[],
    instanceId: string,
    depthLevel: number = 0,
  ) => {
    const conditionals =
      this.getConditionalsByValues(
        typeField,
        idx,
        values,
        conditionalFieldsByField,
      ) || []

    return conditionals
      .filter(f => f.isShown)
      .flatMap(condField => [
        ...this.getFormModelsFromTypeField(condField, instanceId, idx),
        ...this.getConditionalFormFields(
          condField,
          instanceId,
          idx,
          depthLevel + 1,
        ),
      ])
  }

  public getAvailableConditionalFields = (
    typeField: IPermitTypeField,
    fieldIndex: number,
    parentInstanceId?: string,
    parentValueIndex?: number,
  ): IPermitTypeField[] => {
    if (!this.isConditionalField(typeField)) {
      return []
    }

    const conditionalFieldsByField = this.conditionalFields.filter(
      cf => cf.fieldId === typeField.id,
    )

    if (!conditionalFieldsByField.length) {
      return []
    }

    const values = this.getFieldValues<any>(
      typeField.id,
      null,
      null,
      false,
      parentInstanceId,
      parentValueIndex,
    )

    return (
      this.getConditionalsByValues(
        typeField,
        fieldIndex,
        values,
        conditionalFieldsByField,
      ) || []
    )
  }

  public getAnswerByChecklistItemId = (
    typeField: IPermitTypeField,
    checklistItemId: string,
    parentInstanceId: string,
    parentValueIndex: number,
  ): IChecklistAnswerItem => {
    return this.getFieldValues<IChecklistAnswerItem>(
      typeField.id,
      null,
      null,
      false,
      parentInstanceId,
      parentValueIndex,
    ).find(item => item.checklistItemId === checklistItemId)
  }

  public isFieldChanged = (
    fieldId: string,
    index?: number,
    tableId?: string,
    tableRowIndex?: number,
    parentInstanceId?: string,
    parentValueIndex?: number,
  ): boolean => {
    if (!this.existingEntity?.id) {
      return false
    }

    const idsToCheck = [fieldId, tableId]
    if (this.fieldIdsToIgnore.some(id => idsToCheck.includes(id))) {
      return false
    }

    const existingValues = this.getFieldValues<any>(
      fieldId,
      tableId,
      tableRowIndex,
      true,
      parentInstanceId,
      parentValueIndex,
    )
    const values = this.getFieldValues<any>(
      fieldId,
      tableId,
      tableRowIndex,
      false,
      parentInstanceId,
      parentValueIndex,
    )

    if (
      (tableId && !Number.isFinite(tableRowIndex)) ||
      !Number.isFinite(index)
    ) {
      return (
        JSON.stringify(existingValues?.map(normalizeLocationForComparison)) !==
        JSON.stringify(values?.map(normalizeLocationForComparison))
      )
    }

    return (
      JSON.stringify(normalizeLocationForComparison(existingValues[index])) !==
      JSON.stringify(normalizeLocationForComparison(values[index]))
    )
  }

  public isFieldValid = (
    typeField: IPermitTypeField,
    index?: number,
    tableId?: string,
    tableRowIndex?: number,
    parentInstanceId?: string,
    parentValueIndex?: number,
  ): boolean => {
    const hasInvalidState = this.hasInvalidState(
      typeField.id,
      index,
      tableRowIndex,
      parentInstanceId,
      parentValueIndex,
    )

    if (hasInvalidState) {
      return false
    }
    if (
      !typeField.isMandatory ||
      this.isAdminOrFormsMaster ||
      this.isValidationIgnored(
        typeField.id,
        index,
        tableId,
        tableRowIndex,
        parentInstanceId,
        parentValueIndex,
      )
    ) {
      return true
    }

    if (tableId && !Number.isFinite(tableRowIndex)) {
      const tableRows = this.getTableRowsValues(
        tableId,
        typeField.id,
        false,
        parentInstanceId,
        parentValueIndex,
      )

      return (
        tableRows.length &&
        tableRows.every(
          row =>
            row.length &&
            row.every(({ values }) =>
              this.areFieldValuesValid(typeField, values, index),
            ),
        )
      )
    }

    const fieldValues = this.getFieldValues<any>(
      typeField.id,
      tableId,
      tableRowIndex,
      false,
      parentInstanceId,
      parentValueIndex,
    )

    return this.areFieldValuesValid(typeField, fieldValues, index)
  }

  @action.bound
  public addAttachmentValue(
    attachment: IAttachment,
    typeField: IPermitTypeField,
    parentInstanceId?: string,
    parentValueIndex?: number,
  ) {
    const field = this.editableEntity.getField(
      typeField.id,
      parentInstanceId,
      parentValueIndex,
    )

    if (!field) {
      return this.changeFieldValue(
        typeField,
        0,
        attachment,
        parentInstanceId,
        parentValueIndex,
      )
    }

    field.values.push(attachment)
  }

  @action.bound
  public addNewFieldValue<T>(
    typeField: IPermitTypeField,
    initialValue: T,
    parentInstanceId?: string,
    parentValueIndex?: number,
  ) {
    if (!typeField || !this.editableEntity) {
      return
    }

    const { id, type } = typeField
    const { getField, fields } = this.editableEntity
    const field = getField(typeField.id, parentInstanceId, parentValueIndex)

    if (!field) {
      fields.push({
        instanceId: BsonObjectId.generate(),
        fieldId: id,
        type,
        values: [initialValue, initialValue],
        parentInstanceId,
        parentValueIndex,
      })
      return
    }

    if (!field.values.length) {
      field.values.push(initialValue, initialValue)
    } else {
      field.values.push(initialValue)
    }
  }

  @action.bound
  public removeFieldValue(
    fieldId: string,
    index: number,
    parentInstanceId?: string,
    parentValueIndex?: number,
  ) {
    if (!fieldId || !this.editableEntity) {
      return
    }

    const field = this.editableEntity.getField(
      fieldId,
      parentInstanceId,
      parentValueIndex,
    )

    if (field?.values) {
      field.values.splice(index, 1)

      this.removeRelatedConditionals(field.instanceId, index)
    }
  }

  @action.bound
  public clearFieldValues(
    fieldId: string,
    parentInstanceId?: string,
    parentValueIndex?: number,
  ) {
    if (!fieldId || !this.editableEntity) {
      return
    }

    const field = this.editableEntity.getField(
      fieldId,
      parentInstanceId,
      parentValueIndex,
    )

    if (field?.values) {
      field.values.forEach((_, idx) => {
        this.removeRelatedConditionals(field.instanceId, idx, true)
      })

      field.values = []
    }
  }

  @action.bound
  public changeTableFieldValue<T>(
    tableId: string,
    rowIndex: number,
    typeField: IPermitTypeField,
    fieldIndex: number,
    newValue: T,
    parentInstanceId?: string,
    parentValueIndex?: number,
  ) {
    const { getField, fields } = this.editableEntity
    const field = getField(tableId, parentInstanceId, parentValueIndex)

    if (!field) {
      const tableField: PermitField = {
        instanceId: BsonObjectId.generate(),
        fieldId: typeField.id,
        type: typeField.type,
        values: [newValue],
      }

      fields.push({
        instanceId: BsonObjectId.generate(),
        fieldId: tableId,
        type: PermitFieldType.Table,
        values: [[tableField]],
        parentInstanceId,
        parentValueIndex,
      })
      return
    }

    const tableField = (field.values as PermitField[][])[rowIndex]?.find(
      f => f.fieldId === typeField.id,
    )

    if (!tableField) {
      field.values[rowIndex].push({
        instanceId: BsonObjectId.generate(),
        fieldId: typeField.id,
        type: typeField.type,
        values: [newValue],
      } as PermitField)
      return
    }

    tableField.values[fieldIndex] = newValue
  }

  @action.bound
  public changeFieldValue<T>(
    typeField: IPermitTypeField,
    fieldIndex: number,
    newValue: T,
    parentInstanceId?: string,
    parentValueIndex?: number,
  ) {
    const { getField, fields } = this.editableEntity
    const field = getField(typeField.id, parentInstanceId, parentValueIndex)

    if (!field) {
      fields.push({
        instanceId: BsonObjectId.generate(),
        fieldId: typeField.id,
        type: typeField.type,
        values: [newValue],
        parentInstanceId,
        parentValueIndex,
      })
    } else {
      field.values[fieldIndex] = newValue
    }

    this.updateDependentFields(typeField.id)
  }

  // Only the first location carries the pin: a value moved off the first slot drops its own.
  @action.bound
  public changeLocationFieldValues(
    typeField: IPermitTypeField,
    values: ISiteLocation[],
    parentInstanceId?: string,
    parentValueIndex?: number,
  ) {
    this.changeMultipleFieldValues(
      typeField,
      values.map((value, index) =>
        index && value?.preciseLocation
          ? { ...value, preciseLocation: null }
          : value,
      ),
      parentInstanceId,
      parentValueIndex,
    )
  }

  @action.bound
  public changePrimaryLocationValue(
    typeField: IPermitTypeField,
    next: ISiteLocation,
    parentInstanceId?: string,
    parentValueIndex?: number,
  ) {
    if (!typeField.isMultiple) {
      this.changeFieldValue(
        typeField,
        0,
        next,
        parentInstanceId,
        parentValueIndex,
      )
      return
    }
    const values = this.getFieldValues<ISiteLocation>(
      typeField.id,
      undefined,
      undefined,
      false,
      parentInstanceId,
      parentValueIndex,
    )
    this.changeLocationFieldValues(
      typeField,
      withPrimaryLocation(values ?? [], next),
      parentInstanceId,
      parentValueIndex,
    )
  }

  @action.bound
  public changeMultipleFieldValues<T>(
    typeField: IPermitTypeField,
    values: T[],
    parentInstanceId?: string,
    parentValueIndex?: number,
  ) {
    let field = this.editableEntity.getField(
      typeField.id,
      parentInstanceId,
      parentValueIndex,
    )

    if (!field) {
      this.changeFieldValue(
        typeField,
        0,
        '',
        parentInstanceId,
        parentValueIndex,
      )
    }

    field = this.editableEntity.getField(
      typeField.id,
      parentInstanceId,
      parentValueIndex,
    )

    field.values = values

    this.updateDependentFields(typeField.id)
  }

  @action.bound
  public setChecklistAnswer(
    typeField: IPermitTypeField,
    checklistItemId: string,
    answer: boolean,
    parentInstanceId?: string,
    parentValueIndex?: number,
  ) {
    const field = this.editableEntity.getField(
      typeField.id,
      parentInstanceId,
      parentValueIndex,
    )

    if (!field) {
      return this.changeFieldValue(
        typeField,
        0,
        {
          checklistItemId,
          answer,
        } as IChecklistAnswerItem,
        parentInstanceId,
        parentValueIndex,
      )
    }

    const aItem = this.getAnswerByChecklistItemId(
      typeField,
      checklistItemId,
      parentInstanceId,
      parentValueIndex,
    )
    if (aItem) {
      aItem.answer = answer
    } else {
      field.values.push({ checklistItemId, answer })
    }
  }

  @action.bound
  public changeValidationState(
    fieldId: string,
    fieldIndex: number,
    isValid: boolean,
    tableRowIndex?: number,
    parentInstanceId?: string,
    parentValueIndex?: number,
  ) {
    const fieldValidationId = getFieldValidationId(
      fieldId,
      fieldIndex,
      tableRowIndex,
      parentInstanceId,
      parentValueIndex,
    )

    if (isValid) {
      this.invalidFieldsState.delete(fieldValidationId)
    } else {
      this.invalidFieldsState.add(fieldValidationId)
    }
  }

  @action.bound
  public rearrangeInvalidState(
    fieldId: string,
    valueIndex: number,
    parentInstanceId?: string,
    parentValueIndex?: number,
  ) {
    if (!fieldId || !this.editableEntity) {
      return
    }

    this.changeValidationState(
      fieldId,
      valueIndex,
      true,
      null,
      parentInstanceId,
      parentValueIndex,
    )

    const fieldValues = this.getFieldValues<any>(
      fieldId,
      null,
      null,
      false,
      parentInstanceId,
      parentValueIndex,
    )

    fieldValues.forEach((_, idx) => {
      if (idx <= valueIndex) {
        return
      }
      const existingValId = getFieldValidationId(
        fieldId,
        idx,
        null,
        parentInstanceId,
        parentValueIndex,
      )
      if (!this.invalidFieldsState.has(existingValId)) {
        return
      }

      this.invalidFieldsState.delete(existingValId)

      const newValId = getFieldValidationId(
        fieldId,
        idx - 1,
        null,
        parentInstanceId,
        parentValueIndex,
      )
      this.invalidFieldsState.add(newValId)
    })
  }

  @action.bound
  public resetValidationState() {
    this.invalidFieldsState.clear()
  }

  @action.bound
  public toggleSection(id: string) {
    if (this.hiddenFieldsState.has(id)) {
      this.hiddenFieldsState.delete(id)
    } else {
      const idsToHide = getDescendants<IPermitTypeField>(
        this.availableWorkflowSteps
          .flatMap(s => s.fields)
          .map(f => ({
            id: f.id,
            parent: f.parentId,
            text: '',
          })) || [],
        id,
      ).map(node => node.id.toString())

      this.hiddenFieldsState.set(id, idsToHide)
    }
  }

  @action.bound
  public toggleStepSection(id: string) {
    if (this.hiddenFieldsState.has(id)) {
      this.hiddenFieldsState.delete(id)
    } else {
      this.hiddenFieldsState.set(id, [])
    }
  }

  @action.bound
  public expandAllSections() {
    this.hiddenFieldsState.clear()
  }

  public isFieldHidden = (id: string): boolean => {
    return this.allHiddenFieldIds.some(i => i === id)
  }

  public isSectionHidden = (id: string): boolean => {
    return this.hiddenFieldsState.has(id)
  }

  public getTableFields = (tableId: string): IPermitTypeField[] => {
    return (
      this.conditionalFields
        .find(
          ({ fieldId, key }) =>
            fieldId === tableId && key === PERMIT_TABLE_FIELDS_KEY,
        )
        ?.values?.filter(f => f.isShown) || []
    )
  }

  protected getFormFieldModel = (
    typeField: IPermitTypeField,
    parentInstanceId?: string,
    parentValueIndex?: number,
  ): IPermitFormField => {
    const isTable = typeField.type === PermitFieldType.Table
    const isChanged =
      !isTable &&
      this.isFieldChanged(
        typeField.id,
        null,
        null,
        null,
        parentInstanceId,
        parentValueIndex,
      )
    const isValid = isTable
      ? true
      : this.isFieldValid(
          typeField,
          null,
          null,
          null,
          parentInstanceId,
          parentValueIndex,
        )

    return {
      id: typeField.id,
      type: typeField.type,
      caption: typeField.caption,
      isRequired: typeField.isMandatory,
      checklistItems: CHECKLIST_TYPES.includes(typeField.type)
        ? typeField.checklist?.list || []
        : [],
      isChanged,
      isValid,
      parentInstanceId,
      parentValueIndex,
    }
  }

  protected getTableFieldModels(
    tableField: IPermitTypeField,
    parentInstanceId?: string,
    parentValueIndex?: number,
  ): IPermitFormField[] {
    if (tableField.type !== PermitFieldType.Table) {
      return []
    }

    return this.getTableFields(tableField.id).map(field => ({
      id: field.id,
      type: field.type,
      caption: field.caption,
      isRequired: field.isMandatory,
      isChanged: this.isFieldChanged(
        field.id,
        null,
        tableField.id,
        null,
        parentInstanceId,
        parentValueIndex,
      ),
      isValid: this.isFieldValid(
        field,
        null,
        tableField.id,
        null,
        parentInstanceId,
        parentValueIndex,
      ),
      tableId: tableField.id,
      parentInstanceId,
      parentValueIndex,
    }))
  }

  private getFormModelsFromTypeField = (
    typeField: IPermitTypeField,
    parentInstanceId?: string,
    parentValueIndex?: number,
  ): IPermitFormField[] => {
    const fields = [
      this.getFormFieldModel(typeField, parentInstanceId, parentValueIndex),
    ]

    if (typeField.type === PermitFieldType.Table) {
      fields.push(
        ...this.getTableFieldModels(
          typeField,
          parentInstanceId,
          parentValueIndex,
        ),
      )
    }

    return fields
  }

  private getConditionalsByValues(
    typeField: IPermitTypeField,
    fieldIndex: number,
    values: any[],
    conditionalFieldsByField: IConditionalField[],
  ) {
    if (typeField.type === PermitFieldType.Question) {
      return this.getConditionalsForQuestion(
        typeField,
        values,
        conditionalFieldsByField,
      )
    }
    if (typeField.type === PermitFieldType.Checklist) {
      return this.getConditionalsForChecklist(
        typeField,
        values,
        conditionalFieldsByField,
      )
    }
    if (locationPermitFieldTypes.includes(typeField.type)) {
      return conditionalFieldsByField.find(
        cf =>
          values?.[fieldIndex]?.id &&
          matchesConditionalKey(cf.key, values[fieldIndex].id),
      )?.values
    }

    if (materialPermitFieldTypes.includes(typeField.type)) {
      return this.getConditionalsForMaterial(
        conditionalFieldsByField,
        values,
        fieldIndex,
      )
    }

    if (materialResourcePermitFieldTypes.includes(typeField.type)) {
      return this.getConditionalsForMaterialResource(
        conditionalFieldsByField,
        values,
        fieldIndex,
      )
    }

    return conditionalFieldsByField.find(
      cf => values?.[fieldIndex] && cf.key === values[fieldIndex],
    )?.values
  }

  private getConditionalsForMaterial(
    conditionalFieldsByField: IConditionalField[],
    values: any[],
    fieldIndex: number,
  ) {
    return conditionalFieldsByField.find(
      cf => values?.[fieldIndex] && cf.key === FORM_MATERIAL_FIELDS_KEY,
    )?.values
  }

  private getConditionalsForMaterialResource(
    conditionalFieldsByField: IConditionalField[],
    values: any[],
    fieldIndex: number,
  ) {
    const associated =
      conditionalFieldsByField.find(
        cf => cf.key === FORM_MATERIAL_RESOURCE_ASSOCIATED_FIELDS_KEY,
      )?.values || []

    const selectedId = values?.[fieldIndex]?.locationId
    const dependent =
      conditionalFieldsByField.find(cf =>
        selectedId
          ? matchesConditionalKey(cf.key, selectedId)
          : cf.key === FORM_MATERIAL_RESOURCE_FIELDS_KEY &&
            values?.[fieldIndex],
      )?.values || []

    return [...associated, ...dependent]
  }

  private getConditionalsForQuestion(
    typeField: IPermitTypeField,
    permitFieldValues: IChecklistAnswerItem[],
    conditionals: IConditionalField[],
  ): IPermitTypeField[] {
    const key = getQuestionnaireOptionsByType(
      typeField.checklist.list?.[0]?.questionnaireType as QuestionnaireType,
    ).find(opt => opt.value === permitFieldValues?.[0]?.answer)?.key

    return conditionals.find(cf => cf.key === key)?.values
  }

  private getConditionalsForChecklist(
    typeField: IPermitTypeField,
    permitFieldValues: IChecklistAnswerItem[],
    conditionals: IConditionalField[],
  ): IPermitTypeField[] {
    const isFullyChecked = typeField.checklist.list.every(
      cItem =>
        permitFieldValues?.find(v => v.checklistItemId === cItem.id)?.answer !==
        undefined,
    )

    if (!isFullyChecked) {
      return []
    }

    const areYesAnswerSelected = typeField.checklist.list.every(
      cItem =>
        !!permitFieldValues?.find(v => v.checklistItemId === cItem.id)?.answer,
    )
    const areNoOrNaAnswerSelected = typeField.checklist.list.every(cItem => {
      const item = permitFieldValues?.find(v => v.checklistItemId === cItem.id)
      if (!item) {
        return false
      }
      return item.answer === null || item.answer === false
    })

    if (areYesAnswerSelected) {
      return conditionals.find(
        cf => cf.key === PermitChecklistConditionalKey.AllYes,
      )?.values
    }

    if (areNoOrNaAnswerSelected) {
      return conditionals.find(
        cf => cf.key === PermitChecklistConditionalKey.AllNo,
      )?.values
    }

    return []
  }

  private getTableFieldValues<T>(
    tableId: string,
    fieldId: string,
    tableRowIndex?: number,
    isExistingEntity?: boolean,
    parentInstanceId?: string,
    parentValueIndex?: number,
  ): T[] {
    const entity = isExistingEntity ? this.existingEntity : this.editableEntity
    const tableFieldValues: PermitField[][] =
      entity.getField(tableId, parentInstanceId, parentValueIndex)?.values || []

    return Number.isFinite(tableRowIndex)
      ? tableFieldValues[tableRowIndex]?.find(f => f.fieldId === fieldId)
          ?.values || []
      : tableFieldValues?.flatMap(f =>
          f.filter(fld => fld.fieldId === fieldId).flatMap(tf => tf.values),
        ) || []
  }

  private getTableRowsValues(
    tableId: string,
    fieldId: string,
    isExistingEntity?: boolean,
    parentInstanceId?: string,
    parentValueIndex?: number,
  ): PermitField[][] {
    const entity = isExistingEntity ? this.existingEntity : this.editableEntity
    const tableFieldValues: PermitField[][] = entity.getField(
      tableId,
      parentInstanceId,
      parentValueIndex,
    )?.values

    return (
      tableFieldValues?.map(f => f.filter(fld => fld.fieldId === fieldId)) || []
    )
  }

  private hasInvalidState(
    fieldId: string,
    index?: number,
    tableRowIndex?: number,
    parentInstanceId?: string,
    parentValueIndex?: number,
  ): boolean {
    if (Number.isFinite(index)) {
      return this.invalidFieldsState.has(
        getFieldValidationId(
          fieldId,
          index,
          tableRowIndex,
          parentInstanceId,
          parentValueIndex,
        ),
      )
    }

    return Array.from(this.invalidFieldsState.keys()).some(id =>
      id.includes(fieldId),
    )
  }

  private areFieldValuesValid(
    typeField: IPermitTypeField,
    fieldValues: any[],
    index?: number,
  ): boolean {
    if (typeField.checklist) {
      const checklistValues = fieldValues as IChecklistAnswerItem[]
      return typeField.checklist.list.every(
        cItem =>
          checklistValues?.find(v => v.checklistItemId === cItem.id)?.answer !==
          undefined,
      )
    }

    if (typeField.type === PermitFieldType.Signature) {
      const signatureValues = fieldValues as ISignature[]
      if (Number.isFinite(index)) {
        return !!signatureValues?.[index]?.dataUrl
      }
      return signatureValues?.some(v => !!v?.dataUrl)
    }

    if (Number.isFinite(index)) {
      return !!fieldValues?.[index]
    }

    return fieldValues?.some(v => v)
  }

  private isValidationIgnored(
    fieldId: string,
    index?: number,
    tableId?: string,
    tableRowIndex?: number,
    parentInstanceId?: string,
    parentValueIndex?: number,
  ): boolean {
    return (
      this.canIgnoreValidation &&
      !this.fieldIdsToIgnore.includes(fieldId) &&
      !this.isFieldChanged(
        fieldId,
        index,
        tableId,
        tableRowIndex,
        parentInstanceId,
        parentValueIndex,
      )
    )
  }

  @action.bound
  private removeRelatedConditionals(
    parentInstanceId: string,
    parentValueIndex: number,
    isFullRemoval?: boolean,
  ) {
    this.editableEntity.fields = removeWorkflowConditionals(
      this.editableEntity.fields,
      parentInstanceId,
      parentValueIndex,
      isFullRemoval,
    )
  }

  private get isAdminOrFormsMaster(): boolean {
    return this.state.userActiveProjectSettings.isAdminOrFormsMaster
  }

  private isConditionalField = (typeField: IPermitTypeField): boolean => {
    return (
      (!typeField.isMultiple ||
        materialPermitFieldTypes.includes(typeField.type) ||
        materialResourcePermitFieldTypes.includes(typeField.type)) &&
      !!this.editableEntity &&
      conditionalPermitFields.includes(typeField.type) &&
      !!this.conditionalFields?.length
    )
  }

  @action.bound
  private updateDependentFields(changedFieldId: string): void {
    const dynamicFilterFields = this.filterFieldsDependencyMap[changedFieldId]
    if (!dynamicFilterFields?.length) return

    dynamicFilterFields.forEach(dynamicFilterField => {
      const currentField =
        this.editableEntity.mainFieldsByIdMap[dynamicFilterField.id]
      if (!currentField?.values?.length) return

      const relevantFilters =
        dynamicFilterField.filterOptions?.filter(
          filter => filter.fieldId === changedFieldId,
        ) || []

      if (!relevantFilters.length) return

      const filteredValues = this.getFilteredAvailableValues(
        currentField?.values,
        relevantFilters,
      )

      if (!isEqualDeep(currentField.values, filteredValues)) {
        currentField.values = filteredValues
      }
    })
  }

  // Values are { id } for locations and equipment, { locationId } for materials. Dynamic
  // filters match on the id alone, so a value the location map can't resolve — a material,
  // or a deleted or not-yet-saved location — is judged by its id rather than dropped.
  private getFilteredAvailableValues<
    T extends { id?: string; locationId?: string },
  >(values: T[], filters: ITypeFieldFilterOptions[]): T[] {
    if (!values?.length) return []

    return values.filter(value => {
      const id = value?.id ?? value?.locationId
      if (!id) return true

      const item = this.locationStore.hierarchyAllAttributesMap[id] ?? { id }
      return evaluateFilters(
        filters,
        item,
        this.editableEntity.mainFieldsByIdMap,
      )
    })
  }
}
